import { z } from "zod";

export const SANDBOX_NODE_CAPABILITIES = [
  "workspace:xfs-project-quota",
  "proc:lxcfs",
  "egress:allowlist",
  "rootfs:storage-opt",
  "docker-capable",
] as const;

export type SandboxNodeCapability = (typeof SANDBOX_NODE_CAPABILITIES)[number];

export const sandboxNodeCapabilitySchema = z.enum(SANDBOX_NODE_CAPABILITIES);
export const TRUSTED_DOCKER_SANDBOX_POOL_ID = "trusted-docker";
export const DOCKER_CAPABLE_SANDBOX_TAINT_KEY = "docker-capable";

export const sandboxNodeTaintSchema = z.object({
  effect: z.literal("NoSchedule"),
  key: z.string().min(1),
  value: z.string().optional(),
});

export type SandboxNodeTaint = z.infer<typeof sandboxNodeTaintSchema>;

export const sandboxDrainStateSchema = z.enum([
  "online",
  "reconciling",
  "draining",
  "shutting_down",
  "offline",
  "unknown",
]);

export const sandboxImageAvailabilitySchema = z.object({
  checkedAt: z.string(),
  digest: z.string().optional(),
  error: z.string().nullable().optional(),
  present: z.boolean(),
  pullable: z.boolean(),
  ref: z.string(),
});

/**
 * The cloud provider a node's `hostIdentity` belongs to. Ownership fencing on
 * physical-host evidence is only safe when the identity is scoped: the same
 * instance-id string can exist in another account or Region.
 *
 * `external_vps` names a host identity namespace, not an evidence source:
 * manual attestation and a future vendor power-state API describe the same
 * physical machine and therefore share the provider.
 */
export const SANDBOX_HOST_PROVIDERS = ["aws_ec2", "external_vps"] as const;

export type SandboxHostProvider = (typeof SANDBOX_HOST_PROVIDERS)[number];

export const sandboxHostProviderSchema = z.enum(SANDBOX_HOST_PROVIDERS);

/** `i-` plus 8 or 17 lowercase hex characters. Nothing else is an instance id. */
const EC2_INSTANCE_ID_PATTERN = /^i-(?:[\da-f]{8}|[\da-f]{17})$/u;

export function isValidEc2InstanceId(
  value: string | null | undefined,
): boolean {
  return typeof value === "string" && EC2_INSTANCE_ID_PATTERN.test(value);
}

/**
 * A provisioning generation of one physical VPS. Reintroducing the hardware
 * after a fence requires a NEW incarnation, so this is the field that makes an
 * old attestation inapplicable to the machine that comes back.
 *
 * 32 lowercase hex characters: long enough that it cannot be guessed or
 * collided with, short enough to fit the bounded persistence columns, and
 * structurally distinguishable from a hostname or an EC2 instance id.
 */
const HOST_INCARNATION_ID_PATTERN = /^[\da-f]{32}$/u;

export function isValidHostIncarnationId(
  value: string | null | undefined,
): value is string {
  return typeof value === "string" && HOST_INCARNATION_ID_PATTERN.test(value);
}

/**
 * Provider-account scope, Region/site, and VM identity as the node may publish
 * them. Bounds match the persistence columns; anything longer would be
 * silently truncated into a DIFFERENT identity, which is the one failure mode
 * an identity check must never have.
 */
const SANDBOX_HOST_SCOPE_PATTERN = /^[\w.:-]{1,32}$/u;
const SANDBOX_HOST_IDENTITY_PATTERN = /^[\w.:-]{1,128}$/u;

export function isValidSandboxHostScopeValue(
  value: string | null | undefined,
): value is string {
  return typeof value === "string" && SANDBOX_HOST_SCOPE_PATTERN.test(value);
}

export function isValidSandboxHostIdentity(
  value: string | null | undefined,
): value is string {
  return typeof value === "string" && SANDBOX_HOST_IDENTITY_PATTERN.test(value);
}

/**
 * What a durable host-fence evidence row asserts about the physical machine.
 *
 * `powered_off_retired` is NOT "temporarily stopped": it asserts the VM must
 * never be resumed, restored from saved state, or powered on unchanged.
 */
export const SANDBOX_HOST_FENCE_ASSERTED_STATES = [
  "destroyed",
  "powered_off_retired",
] as const;

export type SandboxHostFenceAssertedState =
  (typeof SANDBOX_HOST_FENCE_ASSERTED_STATES)[number];

export const sandboxHostFenceAssertedStateSchema = z.enum(
  SANDBOX_HOST_FENCE_ASSERTED_STATES,
);

/**
 * Who produced the terminal observation. The two vendor values are reserved:
 * a future power-state oracle writes the same rows through the same domain
 * operation, so adding it needs a producer, not a redesign.
 */
export const SANDBOX_HOST_FENCE_EVIDENCE_SOURCES = [
  "operator_attestation",
  "vendor_api",
] as const;

export type SandboxHostFenceEvidenceSource =
  (typeof SANDBOX_HOST_FENCE_EVIDENCE_SOURCES)[number];

export const sandboxHostFenceEvidenceSourceSchema = z.enum(
  SANDBOX_HOST_FENCE_EVIDENCE_SOURCES,
);

export const sandboxNodeInventorySnapshotSchema = z.object({
  bootId: z.string(),
  capabilities: z.array(sandboxNodeCapabilitySchema),
  drainState: sandboxDrainStateSchema,
  hostAccountId: z.string().nullable().optional(),
  hostIdentity: z.string().nullable().optional(),
  /**
   * Published by an `external_vps` node as part of its all-or-nothing
   * provenance CLAIM. Null for EC2 and for unscoped nodes. The orchestrator
   * admits it only when it exactly matches the enrolled binding.
   */
  hostIncarnationId: z.string().nullable().optional(),
  hostProvider: sandboxHostProviderSchema.nullable().optional(),
  hostRegion: z.string().nullable().optional(),
  inventory: z.object({
    allocatableCpuMilli: z.number().int().nonnegative(),
    allocatableRamMb: z.number().int().nonnegative(),
    dindRootAllocatableMb: z.number().int().nonnegative(),
    dindRootFreeMb: z.number().int().nonnegative(),
    dindRootTotalMb: z.number().int().nonnegative(),
    imageAvailability: z.array(sandboxImageAvailabilitySchema),
    imageDigestsPresent: z.array(z.string()),
    maxEnvironments: z.number().int().nonnegative(),
    runningEnvironmentCount: z.number().int().nonnegative(),
    workspaceAllocatableMb: z.number().int().nonnegative(),
    workspaceFreeMb: z.number().int().nonnegative(),
    workspaceTotalMb: z.number().int().nonnegative(),
  }),
  nodeId: z.string(),
  observedAt: z.string(),
  ownerLeaseEpoch: z.number().int().nonnegative(),
  poolId: z.string().nullable().optional(),
  schemaVersion: z.literal(1),
  taints: z.array(sandboxNodeTaintSchema).optional(),
  tenantId: z.string().nullable().optional(),
});

export type SandboxNodeInventorySnapshot = z.infer<
  typeof sandboxNodeInventorySnapshotSchema
>;

export function hasSandboxCapabilities(
  offered: readonly string[],
  required: readonly SandboxNodeCapability[],
): boolean {
  const offeredSet = new Set(offered);
  return required.every((capability) => offeredSet.has(capability));
}

export function requiresDockerCapableSandbox(
  required: readonly string[],
): boolean {
  return required.includes("docker-capable");
}

export function hasDockerCapableSandboxTaint(
  taints: readonly SandboxNodeTaint[] | undefined | null,
): boolean {
  return (
    taints?.some(
      (taint) =>
        taint.key === DOCKER_CAPABLE_SANDBOX_TAINT_KEY &&
        taint.effect === "NoSchedule",
    ) === true
  );
}

export function hasTrustedDockerSandboxPlacementMarker(input: {
  capabilities: readonly string[];
  poolId?: string | null;
  taints?: readonly SandboxNodeTaint[] | null;
}): boolean {
  return (
    input.poolId === TRUSTED_DOCKER_SANDBOX_POOL_ID ||
    input.capabilities.includes("docker-capable") ||
    hasDockerCapableSandboxTaint(input.taints)
  );
}

export function isTrustedDockerSandboxNode(input: {
  capabilities: readonly string[];
  hostIdentity?: string | null;
  poolId?: string | null;
  taints?: readonly SandboxNodeTaint[] | null;
  tenantId?: string | null;
}): boolean {
  return (
    input.capabilities.includes("docker-capable") &&
    input.poolId === TRUSTED_DOCKER_SANDBOX_POOL_ID &&
    hasDockerCapableSandboxTaint(input.taints) &&
    hasNonEmptyString(input.hostIdentity)
  );
}

export type SandboxTenantAllowlist =
  | { mode: "all" }
  | { mode: "list"; ids: readonly string[] };

/**
 * Operator allowlist of organization ids a docker-capable node may serve.
 * Empty / unset / `*` / `all` means every organization. A CSV, JSON array, or
 * single id restricts placement to those organizations only. A malformed JSON
 * array is an empty list, not "all".
 */
export function parseSandboxTenantAllowlist(
  value: string | null | undefined,
): SandboxTenantAllowlist {
  const trimmed = value?.trim() ?? "";
  if (trimmed === "" || trimmed === "*" || trimmed.toLowerCase() === "all") {
    return { mode: "all" };
  }
  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return {
          ids: uniqueNormalizedStrings(
            parsed.filter(
              (entry): entry is string => typeof entry === "string",
            ),
          ),
          mode: "list",
        };
      }
    } catch {
      return { ids: [], mode: "list" };
    }
    return { ids: [], mode: "list" };
  }
  return { ids: uniqueNormalizedStrings(trimmed.split(",")), mode: "list" };
}

export function serializeSandboxTenantAllowlist(
  allowlist: SandboxTenantAllowlist,
): string | null {
  if (allowlist.mode === "all") {
    return null;
  }
  // Preserve list-mode even when empty so a malformed pin cannot republish as
  // "serve every organization".
  return allowlist.ids.length === 0 ? "[]" : allowlist.ids.join(",");
}

export function sandboxTenantAllowlistAllows(
  allowlist: SandboxTenantAllowlist,
  organizationId: string | null | undefined,
): boolean {
  const trimmedOrganizationId = organizationId?.trim() ?? "";
  if (trimmedOrganizationId.length === 0) {
    return false;
  }
  if (allowlist.mode === "all") {
    return true;
  }
  return allowlist.ids.includes(trimmedOrganizationId);
}

export function hasSandboxSchedulingEligibility(input: {
  capabilities: readonly string[];
  hostIdentity?: string | null;
  organizationId?: string | null;
  poolId?: string | null;
  requiredNodeCapabilities: readonly SandboxNodeCapability[];
  taints?: readonly SandboxNodeTaint[] | null;
  tenantId?: string | null;
}): boolean {
  if (
    !hasSandboxCapabilities(input.capabilities, input.requiredNodeCapabilities)
  ) {
    return false;
  }
  const dockerCapableProfile = requiresDockerCapableSandbox(
    input.requiredNodeCapabilities,
  );
  const markedForTrustedDocker = hasTrustedDockerSandboxPlacementMarker(input);
  const trustedDockerNode = isTrustedDockerSandboxNode(input);
  if (dockerCapableProfile) {
    return (
      trustedDockerNode &&
      sandboxTenantAllowlistAllows(
        parseSandboxTenantAllowlist(input.tenantId),
        input.organizationId,
      )
    );
  }
  return !markedForTrustedDocker;
}

function uniqueNormalizedStrings(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed === "" || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

function hasNonEmptyString(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * The only things that may authorize an ownership transfer.
 *
 * A higher lease on the same node proves the old incarnation is gone because
 * the node itself said so. The EC2 and VPS sources prove the physical writer
 * is gone. `same_node_offline_grace` is the one elapsed-time source: it is
 * orchestrator-only evidence that the owner node has been unreachable past a
 * threshold comfortably above the offline guard and the rolling-deploy
 * restart window. It exists because the no-provider deployment (no EC2 flag,
 * no VPS enrollment) otherwise waits forever with `no_evidence` when a node
 * goes offline without ever restarting its lease. It is deliberately never
 * consulted when a fence provider is configured — a provider refusal such as
 * `non_terminal_state` or `owner_recently_seen` is direct contradicting
 * evidence and must not be overridden by elapsed time.
 *
 * The VPS sources are durable database evidence about one enrolled host
 * incarnation, not a bare operator override: they are produced only by the
 * audited attestation path (or, later, a vendor power-state oracle) and are
 * re-checked and consumed inside the ownership-transfer transaction.
 */
export const CHANNEL_ENVIRONMENT_OWNER_FENCE_SOURCES = [
  "ec2_instance_absent",
  "ec2_instance_terminated",
  "same_node_higher_lease",
  "same_node_offline_grace",
  "vps_operator_destroyed",
  "vps_operator_powered_off",
  "vps_vendor_destroyed",
  "vps_vendor_powered_off",
] as const;

export type ChannelEnvironmentOwnerFenceSource =
  (typeof CHANNEL_ENVIRONMENT_OWNER_FENCE_SOURCES)[number];

export function channelEnvironmentVpsFenceSource(input: {
  assertedState: SandboxHostFenceAssertedState;
  evidenceSource: SandboxHostFenceEvidenceSource;
}): ChannelEnvironmentOwnerFenceSource {
  const producer =
    input.evidenceSource === "operator_attestation" ? "operator" : "vendor";
  const state =
    input.assertedState === "destroyed" ? "destroyed" : "powered_off";
  return `vps_${producer}_${state}` as ChannelEnvironmentOwnerFenceSource;
}

/**
 * A reference to durable evidence, NOT a decision.
 *
 * Evaluation happens before the runtime lock, so anything it concluded may be
 * false by the time a transfer is attempted: the evidence can expire, the
 * binding can be retired by someone else, the owner can heartbeat, or the
 * target can already have been consumed. The gate therefore hands the transfer
 * transaction the row identities it must re-lock and re-check, and that
 * transaction — not this reference — is what authorizes anything.
 */
export interface ChannelEnvironmentVpsFenceAuthorization {
  bindingId: string;
  evidenceId: string;
  hostIncarnationId: string;
}

/**
 * Evidence is bound to the exact owner incarnation AND the exact physical
 * resource it describes. Either half alone is insufficient: a node-id fence
 * can outlive the lease it was meant to describe, and an instance id without
 * account/Region scope can name a different machine entirely.
 *
 * Provider fields are null for `same_node_higher_lease`, which needs no
 * physical-host evidence.
 */
export interface ChannelEnvironmentOwnerFenceEvidence {
  environmentId: string;
  hostIdentity: string | null;
  observedAt: string;
  ownerBootId: string | null;
  ownerEpoch: number;
  ownerLeaseEpoch: number;
  ownerNodeId: string;
  provider: SandboxHostProvider | null;
  providerAccountId: string | null;
  providerRegion: string | null;
  source: ChannelEnvironmentOwnerFenceSource;
  /**
   * Present only for `external_vps` evidence. Absent on every EC2 and
   * same-node payload, which are unchanged.
   */
  vpsAuthorization?: ChannelEnvironmentVpsFenceAuthorization;
}

/** Stable key for caches and dedupe: full owner tuple plus provider identity. */
export function channelEnvironmentOwnerFenceKey(input: {
  environmentId: string;
  hostIdentity?: string | null;
  ownerBootId?: string | null;
  ownerEpoch?: number | null;
  ownerLeaseEpoch?: number | null;
  ownerNodeId: string;
  provider?: SandboxHostProvider | null;
  providerAccountId?: string | null;
  providerRegion?: string | null;
}): string {
  return [
    input.environmentId,
    input.ownerNodeId,
    input.ownerBootId ?? "",
    String(input.ownerEpoch ?? 0),
    String(input.ownerLeaseEpoch ?? 0),
    input.provider ?? "",
    input.providerAccountId ?? "",
    input.providerRegion ?? "",
    input.hostIdentity ?? "",
  ].join("|");
}
